import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { exploreSurveyIntentPath } from "../../../configs/ExploreSurveyConfig";
import { AuthContext } from "../../../context/AuthContext";
import { ZeosApiContext } from "../../../context/ZeosApiContext";
import { useDataFromParams } from "../../../hooks/useDataFromParams";
import { IZeosUser } from "../../../services/auth/IZeosUser";
import { IPortfolioItem } from "../../../mock-data/portfolioData";
import { handleOpenLinkUtilityAsync } from "../../../services/helpers/UtilityLinkHelpers";
import { PortfolioModalType } from "../../../services/helpers/PortfolioHelpers";
import { pageConfigBySlug } from "../../../services/nav/NavigationService";
import { IPropertyContract } from "../../../services/zeos-api/contracts/Property";
import { UapiStatus } from "../../../services/zeos-api/enums/Uapi";
import { zeosDialog } from "../../Shared/confirm-dialog/ConfirmDialog";
import { ZeosModal } from "../../Shared/ZeosModal";
import { invalidateDashboardOverviewCache } from "../MainDashboard";
import { invalidateNextGenSeriesCache } from "../../NextGenDashboard/NextGenDashboard";
import { DerapiLinkSelector } from "../../utility-link/DerapiLinkSelector";
import { UtilityLinkSelector } from "../../utility-link/UtilityLinkSelector";
import {
  DashboardCtaAction,
  IDashboardTileStatusCopy,
} from "./dashboardTileStatusCopy";

type ConnectKind = "derapi" | "utility";

interface IDashboardConnectContext {
  handleCta: (copy: IDashboardTileStatusCopy) => void;
}

const DashboardConnectContext = createContext<IDashboardConnectContext | null>(
  null
);

const toPortfolioItem = (
  property: IPropertyContract | undefined
): IPortfolioItem | null => {
  if (!property?.propertyId) {
    return null;
  }
  return {
    propertyId: property.propertyId,
    companyId: property.companyId,
    companyName: "",
    image: property.blobImageUrl,
    name: property.propertyName,
  };
};

export const useDashboardConnectCta = () => useContext(DashboardConnectContext);

export const DashboardConnectProvider = (props: {
  children: React.ReactNode;
}) => {
  const navigate = useNavigate();
  const api = useContext(ZeosApiContext);
  const auth = useContext(AuthContext);
  const { property } = useDataFromParams();
  const [zeosUser, setZeosUser] = useState<IZeosUser>();
  const [connectKind, setConnectKind] = useState<ConnectKind | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loadUser = async () => {
      const user = await auth.zeosUserAsync();
      if (!cancelled) {
        setZeosUser(user);
      }
    };
    loadUser();
    return () => {
      cancelled = true;
    };
  }, [auth]);

  const portfolioItem = useMemo(() => toPortfolioItem(property), [property]);

  const closeModal = useCallback(() => setConnectKind(null), []);

  const handleUtilityStatusChange = useCallback(
    (status: UapiStatus) => {
      if (status !== UapiStatus.LINKED || !portfolioItem) {
        return;
      }
      invalidateDashboardOverviewCache(portfolioItem.propertyId);
      invalidateNextGenSeriesCache(portfolioItem.propertyId);
    },
    [portfolioItem]
  );

  const handleCta = useCallback(
    async (copy: IDashboardTileStatusCopy) => {
      const action: DashboardCtaAction = copy.ctaAction;
      if (action === "explore") {
        const exploreSlug = pageConfigBySlug("explore")?.slug || "explore";
        navigate(exploreSurveyIntentPath(exploreSlug, copy.exploreDomain));
        return;
      }

      if (!portfolioItem) {
        zeosDialog("Select a property to connect.");
        return;
      }

      if (action === "utility") {
        const linkType = await handleOpenLinkUtilityAsync(portfolioItem, api);
        if (linkType === PortfolioModalType.None) {
          return;
        }
        setConnectKind("utility");
        return;
      }

      setConnectKind("derapi");
    },
    [api, navigate, portfolioItem]
  );

  const value = useMemo(() => ({ handleCta }), [handleCta]);

  return (
    <DashboardConnectContext.Provider value={value}>
      {props.children}
      <ZeosModal isOpen={connectKind !== null} onClose={closeModal}>
        {connectKind === "derapi" ? (
          <DerapiLinkSelector
            property={portfolioItem}
            zeosUser={zeosUser}
            onClose={closeModal}
          />
        ) : null}
        {connectKind === "utility" ? (
          <UtilityLinkSelector
            property={portfolioItem}
            zeosUser={zeosUser}
            onClose={closeModal}
            onStatusChange={handleUtilityStatusChange}
          />
        ) : null}
      </ZeosModal>
    </DashboardConnectContext.Provider>
  );
};
